import { z } from 'zod';
import { EMPTY_PRODUCT_SPECS, productSpecsSchema } from './productSpecs.js';

export const slugSchema = z
  .string()
  .min(1)
  .max(96)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug hanya boleh huruf kecil, angka, dan tanda hubung');

export const productListQuerySchema = z.object({
  category: slugSchema.optional(),
  type: z.enum(['CONFIGURABLE', 'GENERATOR', 'QUOTE']).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export const productParamsSchema = z.object({
  slug: slugSchema,
});

export const priceRequestSchema = z.object({
  selections: z.record(z.string(), z.string()).default({}),
  qty: z.number().int().min(1).max(1_000_000),
  geometry: z
    .object({
      widthMm: z.number().positive().max(100_000),
      heightMm: z.number().positive().max(100_000),
      cutLengthMm: z.number().positive().max(10_000_000).optional(),
    })
    .optional(),
});

export const publishStatusSchema = z.enum(['DRAFT', 'PUBLISHED', 'ARCHIVED']);
export const productTypeSchema = z.enum(['CONFIGURABLE', 'GENERATOR', 'QUOTE']);
export const priceUnitSchema = z.enum(['PIECE', 'SQUARE_METER', 'LINEAR_METER']);
export const shippingModeSchema = z.enum(['COURIER', 'PICKUP_ONLY', 'QUOTE']);

export const adminProductQuerySchema = z.object({
  status: publishStatusSchema.optional(),
  category: slugSchema.optional(),
  q: z.string().trim().max(120).optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

/** Field yang sama dipakai saat membuat dan menyunting; slug dipisah karena tidak bisa diubah. */
const productFields = {
  name: z.string().trim().min(2).max(160),
  tagline: z.string().trim().max(240),
  description: z.string().trim().max(20_000),
  type: productTypeSchema,
  status: publishStatusSchema,
  categorySlug: slugSchema,

  priceUnit: priceUnitSchema,
  /** Rupiah penuh, bukan sen. Desimal dipakai karena ada satuan per meter persegi. */
  basePrice: z.number().min(0).max(1_000_000_000),
  minQty: z.number().int().min(1).max(1_000_000),
  qtyStep: z.number().int().min(1).max(1_000_000),
  leadTimeDays: z.number().int().min(0).max(365),

  shippingMode: shippingModeSchema,
  weightGram: z.number().int().min(0).max(1_000_000),
  /** false berarti berat flat per pesanan, tidak dikali jumlah. */
  weightPerUnit: z.boolean(),
  lengthCm: z.number().int().min(0).max(10_000).nullable(),
  widthCm: z.number().int().min(0).max(10_000).nullable(),
  heightCm: z.number().int().min(0).max(10_000).nullable(),
  /** Kapasitas satu paket kirim. null berarti berapa pun jumlahnya tetap satu paket. */
  unitsPerPackage: z.number().int().min(1).max(1_000_000).nullable(),

  /** Spesifikasi cetak editor bebas (KEPUTUSAN 4.7), semua dalam mm. */
  printWidthMm: z.number().min(1).max(100_000).nullable(),
  printHeightMm: z.number().min(1).max(100_000).nullable(),
  printBleedMm: z.number().min(0).max(100).nullable(),
  printSafeMm: z.number().min(0).max(1_000).nullable(),
  freeEditor: z.boolean(),

  /** Tabel spesifikasi dan poin singkat yang tampil di panel produk. */
  specs: productSpecsSchema,
  /** Catatan operasional produk, teks bebas multi-baris. Kosong berarti tidak ditampilkan. */
  notes: z.string().trim().max(10_000),

  seoTitle: z.string().trim().max(160),
  seoDescription: z.string().trim().max(320),
  sortOrder: z.number().int().min(0).max(10_000),
};

export const productInputSchema = z.object({
  slug: slugSchema,
  ...productFields,
  tagline: productFields.tagline.default(''),
  description: productFields.description.default(''),
  type: productTypeSchema.default('CONFIGURABLE'),
  status: publishStatusSchema.default('DRAFT'),
  priceUnit: priceUnitSchema.default('PIECE'),
  basePrice: productFields.basePrice.default(0),
  minQty: productFields.minQty.default(1),
  qtyStep: productFields.qtyStep.default(1),
  leadTimeDays: productFields.leadTimeDays.default(2),
  shippingMode: shippingModeSchema.default('COURIER'),
  weightGram: productFields.weightGram.default(0),
  weightPerUnit: z.boolean().default(true),
  lengthCm: productFields.lengthCm.default(null),
  widthCm: productFields.widthCm.default(null),
  heightCm: productFields.heightCm.default(null),
  unitsPerPackage: productFields.unitsPerPackage.default(null),
  printWidthMm: productFields.printWidthMm.default(null),
  printHeightMm: productFields.printHeightMm.default(null),
  printBleedMm: productFields.printBleedMm.default(null),
  printSafeMm: productFields.printSafeMm.default(null),
  freeEditor: z.boolean().default(false),
  specs: productSpecsSchema.default(EMPTY_PRODUCT_SPECS),
  notes: productFields.notes.default(''),
  seoTitle: productFields.seoTitle.default(''),
  seoDescription: productFields.seoDescription.default(''),
  sortOrder: productFields.sortOrder.default(0),
});

/** Semua opsional: hanya field yang dikirim yang diubah. Slug tidak ada di sini. */
export const productUpdateSchema = z
  .object(productFields)
  .partial()
  .refine((data) => Object.keys(data).length > 0, {
    message: 'Tidak ada perubahan yang dikirim',
  });

// --- Kategori (admin) ---

const categoryFields = {
  name: z.string().trim().min(2).max(120),
  tagline: z.string().trim().max(240),
  description: z.string().trim().max(20_000),
  status: publishStatusSchema,
  /** null = kategori akar. Tidak boleh menunjuk dirinya sendiri atau keturunannya. */
  parentSlug: slugSchema.nullable(),
  /** Id MediaAsset untuk gambar utama; null melepas gambar. */
  heroMediaId: z.string().trim().min(1).max(64).nullable(),
  seoTitle: z.string().trim().max(160),
  seoDescription: z.string().trim().max(320),
  sortOrder: z.number().int().min(0).max(100_000),
};

export const categoryInputSchema = z.object({
  slug: slugSchema,
  name: categoryFields.name,
  tagline: categoryFields.tagline.default(''),
  description: categoryFields.description.default(''),
  status: categoryFields.status.default('DRAFT'),
  parentSlug: categoryFields.parentSlug.default(null),
  heroMediaId: categoryFields.heroMediaId.default(null),
  seoTitle: categoryFields.seoTitle.default(''),
  seoDescription: categoryFields.seoDescription.default(''),
  sortOrder: categoryFields.sortOrder.default(0),
});

export const categoryUpdateSchema = z
  .object(categoryFields)
  .partial()
  .refine((data) => Object.keys(data).length > 0, {
    message: 'Tidak ada perubahan yang dikirim',
  });

export const adminCategoryQuerySchema = z.object({
  status: publishStatusSchema.optional(),
});

export type CategoryInput = z.infer<typeof categoryInputSchema>;
export type CategoryUpdate = z.infer<typeof categoryUpdateSchema>;
export type AdminCategoryQuery = z.infer<typeof adminCategoryQuerySchema>;

export type PublishStatus = z.infer<typeof publishStatusSchema>;
export type ProductType = z.infer<typeof productTypeSchema>;
export type AdminProductQuery = z.infer<typeof adminProductQuerySchema>;
export type ProductInput = z.infer<typeof productInputSchema>;
export type ProductUpdate = z.infer<typeof productUpdateSchema>;

/** Menyusun ulang seluruh galeri sekaligus: urutan array menjadi sortOrder. */
export const productMediaSchema = z.object({
  items: z
    .array(
      z.object({
        mediaId: z.string().min(1).max(64),
        isPrimary: z.boolean().default(false),
      }),
    )
    .max(24),
});

export type ProductMediaInput = z.infer<typeof productMediaSchema>;

export type ProductListQuery = z.infer<typeof productListQuerySchema>;
export type ProductParams = z.infer<typeof productParamsSchema>;
export type PriceRequest = z.infer<typeof priceRequestSchema>;
